import * as a from '@mitranim/js/all.mjs'
import * as c from './conf.mjs'
import * as uma from './util-math.mjs'
import * as ut from './util-text.mjs'
import * as ud from './util-dom.mjs'

/*
TODO: find out if there's a way to ensure that Gtag events are sent even if the
user closes the browser tab at the same time. This can be done via either
`navigator.sendBeacon` or via the `keepalive` option in the `fetch` API. Some
older versions of Gtag / GA supported this, via the `{transport: 'beacon'}`
option passed to events. We should find how to do that in the newer version.

When calling `window.gtag` or `window.dataLayer.push`, the first argument must
be one of the known commands recognized by `gtag.js`. The set of commands is
closed. We can't invent new ones. See documentation:

  https://developers.google.com/tag-platform/gtagjs/reference

Also see recommended events for GA:

  https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag
*/
export class Gtag extends a.Emp {
  constructor(ctx) {
    super()
    a.priv(this, `ctx`, a.reqStruct(ctx))
  }

  // Nop for SSR. Override in `ClientGtag`.
  push() {}

  /*
  The `config` command is intentionally disabled here because we only need to
  invoke it once in an HTML inline script, subsequent invocations are similar
  to `set`, and it has a problem: it doesn't appear in the GTM debug view nor
  the GA debug view. For associating key-value pairs with the current session,
  the `set` command is equivalent, and does appear everywhere.

  GTM debug view can be accessed by clicking "Preview" somewhere in
  https://tagmanager.google.com.

  GA debug view can be found somewhere in https://analytics.google.com. Unlike
  the GTM debug view, it shows all events rather than events from one specific
  browser tab, but it's slow and unreliable.

    config() {throw Error(`unused`)}
  */

  /*
  Unused for now.
  Caution: the promise may never resolve.

    get(_key) {
      throw Error(`unused`)
      // a.reqValidStr(key)
      // return new Promise(done => this.push(`get`, c.GA_MEASUREMENT_ID, key, done))
    }
  */

  /*
  We restrict the `set` command to primitive values because of limitations of
  the GA UI: it renders non-primitive values as `[object Object]`. Only
  primitives can actually be viewed in the GA UI.

  See well-known parameters:

    https://developers.google.com/tag-platform/gtagjs/reference/parameters

  Note that the page may lack product-specific parameters for specific Google
  products, particularly Google Analytics.

  TODO consider using this to specify platform, user agent, and so on, because
  the GA UI often fails to display them, even though it has the data.
  */
  set(key, val) {this.push(`set`, a.reqValidStr(key), a.reqPrim(val))}

  /*
  Whenever possible, we should prefer standard / well known events.
  References:

    https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag
    https://developers.google.com/tag-platform/gtagjs/reference/events
  */
  sendEvent(key, opt) {
    a.reqValidStr(key)
    if (a.optDict(opt)) this.push(`event`, key, opt)
    else this.push(`event`, key)
  }

  /*
  See `gtagUser` for the reference on setting arbitrary user properties.
  Also see the comment on `.set` for the gotchas with nested dictionaries.
  */
  setUser(src) {this.push(`set`, gtagUser(src))}

  /*
  Reference about sending page views:

    https://developers.google.com/analytics/devguides/collection/ga4/views?client_type=gtag

  We don't need to use `set` to store `page_title` and `page_location` for other
  subsequent commands, because that's a default built-in behavior anyway. Note
  that the automatically-added `page_title` and `page_location` do not appear
  in GTM debug view, but do appear in GA debug view.

  Important: for page views to function correctly, we must DISABLE GA's hack for
  collecting page views in SPA. GA does it incorrectly, because it captures a
  page view synchronously when `history.pushState` is invoked, producing the
  wrong page title, because `document.title` is updated asynchronously, after
  fetching the data for the page. In addition, while the next page is being
  prepared, the user may click ANOTHER link, in which case we cancel the
  previous page fetch, and the pending page is never viewed, while GA would
  generate a fake page view. Reference:

    https://developers.google.com/analytics/devguides/collection/ga4/single-page-applications?implementation=browser-history
  */
  sendPageView() {
    this.sendEvent(c.GTAG_EVENT_PAGE_VIEW, {
      page_title: document.title,
      page_location: window.location.href,
    })
  }

  /*
  TODO make this actually work. Right now, it wouldn't be actually sent. In
  older versions of Gtag, it was possible to specify `{transport: "beacon"}`
  for the `window.sendBeacon` API which is not canceled by a page reload.
  We should find out if that's possible in current Gtag, or whether it's
  actually needed because Gtag might be able to handle this automatically.

  sendBye() {this.sendEvent(c.GTAG_EVENT_PAGE_UNLOAD)}
  */

  sendEmailSub(opt) {
    this.sendEvent(c.GTAG_EVENT_EMAIL_SUBSCRIPTION, a.onlyDict(opt))
  }

  /*
  Events `c.GTAG_EVENT_SIGN_UP` and `c.GTAG_EVENT_LOGIN` are prescribed by
  Gtag and GA documentation. References:

    https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag
    https://developers.google.com/tag-platform/gtagjs/reference/events

  TODO consider: for analytics reports in GA, it might be more convenient to
  combine them into one event with a field `isCreated`.
  */
  sendAuth({user, isCreated, method}) {
    const {id, isPractitioner, isOrganization, isModerator} = a.reqStruct(user)

    this.setUser(user)

    this.sendEvent(
      a.optBool(isCreated) ? c.GTAG_EVENT_SIGN_UP : c.GTAG_EVENT_LOGIN,
      {method, id, isPractitioner, isOrganization, isModerator},
    )
  }

  /*
  Detects if we've JUST performed an authentication redirect, and sends a
  relevant Gtag event if we did. The server makes this possible by adding
  relevant query entries in the redirect URL.
  */
  async detectAuth() {
    const ctx = this.ctx
    const url = ctx.url

    if (!a.isSome(ut.queryPop(url, c.QUERY_KEY_AUTHED))) return
    const isCreated = a.boolOpt(ut.queryPop(url, c.QUERY_KEY_IS_PERSON_CREATED))
    const method = ut.queryPop(url, c.QUERY_KEY_IDENT_PROVIDER) || c.IDENT_PROVIDER_LOCAL

    // This may be `practitioner` or `organization`. We don't care. We just
    // fetch the user and pick relevant fields.
    url.query.delete(c.QUERY_KEY_MODE)

    // Set a cleaner URL. This should be done synchronously before fetching any
    // data in the current method.
    ud.historyReplace(url)

    const user = await ctx.dat.user.valueTask()
    this.sendAuth({user, isCreated, method})
  }

  sendEventCreate(ent) {
    const opt = gtagOptFromEnt(ent)
    if (opt) this.sendEvent(c.GTAG_EVENT_CREATE, opt)
  }

  /*
  https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#purchase
  https://developers.google.com/tag-platform/gtagjs/reference/events#purchase
  */
  sendPurchase(opt) {
    this.sendEvent(c.GTAG_EVENT_PURCHASE, reqValidGtagPurchaseOpt(opt))
  }
}

export class ClientGtag extends Gtag {
  /*
  ONLY `Arguments` WORK. `Array` does NOT work. The Gtag JS script IGNORES any
  entries in `window.dataLayer` which are not `arguments`.
  */
  push() {
    a.reqValidStr(arguments[0])
    if (!c.DOM_AVAILABLE) return
    if (!c.EXTERNAL_INTEGRATIONS_ENABLED) return

    const tar = a.reqArr(window.dataLayer ||= [])

    /*
    Precaution against infinite memory leaks when Gtag is missing or broken, for
    example due to adblockers.

    The overridden `.push` created by Gtag limits the size of the array to 1000,
    involving the following clown code:

      Math.max(100, Number("1000") || 300)

    Our limit should not affect Gtag.
    */
    if (tar.length > 1024) return

    tar.push(arguments)
  }
}

/*
Should be used for setting user fields in Gtag / GA. When rendering in SSR, we
should provide these fields to the inline-script `config` command that
initializes Gtag. When logging in or logging out, this should be used for a
`set` command to dynamically update user fields. This dictionary of fields
should NOT be trimmed. Fields with nil values should still be present, for the
purpose of overriding previous values after logging in or out.
*/
export function gtagUser(src) {
  a.optStruct(src)

  return {
    /*
    Google Analytics has special support for user ID. Reference:

      https://developers.google.com/analytics/devguides/collection/ga4/user-id?client_type=gtag
    */
    user_id: src?.id,

    /*
    Google Analytics has special support for arbitrary user properties.
    Reference:

      https://developers.google.com/analytics/devguides/collection/ga4/reference/config#user_properties
    */
    user_properties: a.pickKeys(src, [
      /*
      By default, the GA UI replaces all emails with "(redacted)".
      To actually see user emails, we must disable censorship:

        Admin → Data collection and modification → Data streams → Web
        → XXX → Redact data → uncheck "Email" → Save
      */
      `email`,

      `isPractitioner`,
      `isOrganization`,
      `isModerator`,
    ]),
  }
}

/*
References:

  https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#purchase
  https://developers.google.com/tag-platform/gtagjs/reference/events#purchase

TODO consider validating `currency` and `value`.
TODO consider validating `transaction_id` (payment subject ID).
*/
function reqValidGtagPurchaseOpt(val) {
  // if (c.PUBLIC_RELEASE) return val
  a.reqDict(val)
  a.reqValidStr(val.entType)
  a.reqArr(val.items)
  reqValidGtagItem(val.items[0])
  return val
}

/*
References:

  https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_item_list_item
  https://developers.google.com/tag-platform/gtagjs/reference/events#view_item_list_item

Warning: GA debug UI shows only standard / predefined item fields. Custom fields
are COMPLETELY ignored. When sending purchase-related events, all custom fields
must be set in event root.
*/
export function isValidGtagItem(val) {return a.isObj(val) && a.isValidStr(val.item_id)}
export function optValidGtagItem(val) {return a.opt(val, isValidGtagItem)}
export function reqValidGtagItem(val) {return a.req(val, isValidGtagItem)}

/*
GA UI displays arbitrary custom fields in event root, but only predefined fields
in individual items, for events that support `.items`. Our entity classes
provide custom fields in one method, and predefined item fields in another
method, to support this separation for events with items. For events without
predefined support for `.items`, we have to dump everything into event root,
because otherwise GA may not display the item at all.
*/
export function gtagOptFromEnt(src) {
  a.optStruct(src)
  if (!c.GTAG_ENABLED || !src) return undefined

  const out = a.Emp()
  a.assign(out, src.gtagOpt?.())
  a.assign(out, src.gtagItem?.())
  return out
}

export function gtagItemsOptFromEnt(src) {
  return a.optStruct(src) && gtagItemsOptFromEnts([src])
}

export function gtagItemsOptFromEnts(ents) {
  a.optIter(ents)
  if (!c.GTAG_ENABLED || !ents) return undefined

  const opt = a.Emp()
  const items = []

  for (const ent of ents) {
    a.assign(opt, a.optDict(ent?.gtagOpt?.()))
    const item = ent?.gtagItem?.()
    if (item) items.push(item)
  }

  if (!items.length) return undefined
  opt.items = items
  return opt
}

// GA documentation suggests using uppercase codes for currency.
export function currencyToGtag(val) {return a.vac(a.upper(val))}

/*
GA documentation states that money should be specified in full units, with
decimal fractions as needed. In purchase-related events, the field `.value` in
event root is displayed correctly, but the field `.price` in items is
multiplied by 1_000_000. Converting this value to a string does NOT prevent GA
from multiplying `.price`. Unclear how to prevent that.
*/
export function centsToGtag(val) {return uma.moneyCentsToUnits(val)}
